/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { MaskDefinitionService } from '../mask-definition.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { MaskDefinitionDaoService } from '../../dao/mask-definition-dao.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { MaskDefinition } from 'src/app/_model/mask-definition';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class MaskDefinitionLogicService implements MaskDefinitionService {
  public maskDefinitions$ = new BehaviorSubject<MaskDefinition>(undefined);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private maskDefinitionDaoService: MaskDefinitionDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.maskDefinitionDaoService.reset();
  }

  public async clear() {
    this.maskDefinitionDaoService.clear();
    this.maskDefinitions$.next(undefined);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteMaskDefinitions(apiLevel, syncKey);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalMaskDefinitions();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalMaskDefinitions() {
    try {
      this.logger.log(this.constructor.name, '- Load local MaskDefinitions');

      let masks: MaskDefinition = new MaskDefinition(
        await this.maskDefinitionDaoService.getMaskDefinitionsData(),
      );
      this.logger.log(this.constructor.name, '- Mask Definitions:', masks);
      this.maskDefinitions$.next(masks);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local MaskDefinitions: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteMaskDefinitions(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new Mask Definition from IMS',
      );

      var masks: MaskDefinition;
      try {
        let data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'maskdefinition',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey,
            }
          }),
        );

        this.logger.log(this.constructor.name, 'Downloaded Data:', data);

        masks = new MaskDefinition(data);

        this.logger.log(
          this.constructor.name,
          'Downloaded Mask Definition: ',
          masks,
        );

        await this.maskDefinitionDaoService.saveMaskDefinitionsData(masks.data);
        this.maskDefinitions$.next(masks);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Mask Definition: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  async initializeCompleted(): Promise<void> {}
}
